/**
 * NotificationDropdown Component
 *
 * Dropdown panel that displays notifications.
 *
 * Features:
 * - Displays list of notifications ordered by newest first
 * - "Mark all as read" button
 * - Empty state when no notifications
 * - Scroll support for long lists
 * - Loading and error states
 *
 * @module components/NotificationDropdown
 */

import { NotificationItem } from './NotificationItem';
import {
  useNotificationsGraphQL,
  useMarkAllNotificationsReadGraphQL,
} from '@/graphql/hooks/useNotificationsGraphQL';
import logger from '@/utils/logger';
import { cn } from '@/lib/utils';

export interface NotificationDropdownProps {
  isOpen: boolean;
  onClose?: () => void;
  className?: string;
}

/**
 * NotificationDropdown - Dropdown panel for notifications
 *
 * Usage:
 *   <NotificationDropdown isOpen={isDropdownOpen} onClose={() => setIsOpen(false)} />
 */
export function NotificationDropdown({ isOpen, onClose, className }: NotificationDropdownProps) {
  // Fetch notifications (only when dropdown is open) using GraphQL
  const {
    notifications,
    loading: isLoading,
    error,
    refetch,
  } = useNotificationsGraphQL({
    limit: 50,
    enabled: isOpen, // Only fetch when dropdown is open
  });

  // Mark all as read mutation (using GraphQL)
  const { markAllRead, loading: markAllLoading } = useMarkAllNotificationsReadGraphQL();

  const handleMarkAllRead = async () => {
    const result = await markAllRead();
    if (result) {
      logger.info('Notifications', 'Marked all notifications as read via GraphQL', {
        updated: result.updated,
      });
      // Refetch to refresh notification list
      await refetch();
    }
  };
  const hasUnread = notifications.some((n) => n.status === 'unread');

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={cn(
        'absolute right-0 top-full mt-2',
        'w-96 max-h-[600px]',
        'bg-surface-1 rounded-lg shadow-lg',
        'border border-border',
        'overflow-hidden',
        'z-50',
        className
      )}
      role="dialog"
      aria-label="Notifications"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-surface-2">
        <h2 className="text-sm font-semibold text-foreground">Notifications</h2>
        {hasUnread && (
          <button
            onClick={handleMarkAllRead}
            disabled={markAllLoading}
            className="text-xs text-primary hover:text-primary-hover transition-colors disabled:opacity-50"
            aria-label="Mark all as read"
          >
            {markAllLoading ? 'Marking...' : 'Mark all read'}
          </button>
        )}
      </div>

      {/* Content */}
      <div className="overflow-y-auto max-h-[500px]">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted">Loading notifications...</div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-danger">Failed to load notifications</div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && notifications.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 text-muted mb-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="text-sm text-muted font-medium">No notifications</div>
            <div className="text-xs text-muted mt-1">You're all caught up!</div>
          </div>
        )}

        {/* Notification List */}
        {!isLoading && !error && notifications.length > 0 && (
          <div className="divide-y divide-border">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.globalId}
                notification={notification}
                onClose={onClose}
                className="border-none"
              />
            ))}
          </div>
        )}
      </div>

      {/* Footer (optional - for "View all" link) */}
      {notifications.length > 0 && (
        <div className="px-4 py-3 border-t border-border bg-surface-2 text-center">
          <a
            href="/notifications"
            className="text-xs text-primary hover:text-primary-hover transition-colors"
          >
            View all notifications
          </a>
        </div>
      )}
    </div>
  );
}

export default NotificationDropdown;
